import {
  InspectionSecurityStatusEnum,
  AssetTypesEnum,
  BankSecurityCheckEnum,
  NevdisSecurityCheckEnum,
  RegoSecurityCheckEnum,
  InspectionRule,
  SECURITY_CHECK_TYPE_TO_RULE_TYPE,
  SECURITY_CHECK_RULE_TYPE_ALIASES,
  REGO_CERTIFICATE_DATA_EDIT_TYPE,
  BANK_STATEMENT_DATA_EDIT_TYPE
} from '@types';
import { 
  INSPECTION_SECURITY_CHECK_VARIANT,
  INSPECTION_SECURITY_CHECK_LABEL_STATUS,
  SECURITY_CHECK_LIGHT,
  SECURITY_CHECK_LIST
} from '@constants';

/**
 * Resolves the inspectionChecks object key for a given step and UI key.
 * Step 3 (rego) and step 4 (bank) use different property names than the UI keys.
 */
export function getInspectionCheckItemKey(
  activeStep: string,
  key: string
): string {
  if (activeStep === '3') {
    if (key === 'expiry') return 'registrationExpiry';
    if (key === 'registrationAuthenticity') return 'regoCertificate';
  }
  if (activeStep === '4') {
    if (key === 'BANK_STATEMENT_ADDRESS') return 'address';
    return 'bankStatement';
  }
  return key;
}

export function resolvePpsrCheckDocument(items: any, assetType?: string): any {
  if (!items) return null;
  if (assetType === AssetTypesEnum.MARINE) {
    return items.marinePpsr ?? items.ppsr;
  }
  if (assetType === AssetTypesEnum.MARINE_WITH_TRAILER) {
    const requestedChecks = [items.ppsr, items.marinePpsr].filter(
      (check) =>
        check &&
        check.status !== InspectionSecurityStatusEnum.notRequested
    );

    if (requestedChecks.length === 0) {
      return items.ppsr ?? items.marinePpsr;
    }
    if (requestedChecks.length === 1) {
      return requestedChecks[0];
    }

    const ppsrData = requestedChecks.flatMap((check) => check.ppsrData ?? []);
    const hasRecord =
      ppsrData.length > 0 ||
      requestedChecks.some(
        (check) =>
          check.status === InspectionSecurityStatusEnum.reportFound
      );
    const allChecksCompleted = requestedChecks.every(
      (check) => check.status === InspectionSecurityStatusEnum.noRecord
    );
    const hasError = requestedChecks.some(
      (check) => check.status === InspectionSecurityStatusEnum.error
    );

    return {
      ...requestedChecks[0],
      ...requestedChecks[1],
      type: requestedChecks[0].type,
      status: hasRecord
        ? InspectionSecurityStatusEnum.reportFound
        : allChecksCompleted
          ? InspectionSecurityStatusEnum.noRecord
          : hasError
            ? InspectionSecurityStatusEnum.error
            : InspectionSecurityStatusEnum.notPerformed,
      ppsrData: ppsrData.length > 0 ? ppsrData : undefined
    };
  }
  return items.ppsr;
}

export function shouldShowPayoutLetterSection(
  documentTypes: string[] = [],
  items?: any,
  assetType?: string
): boolean {
  return (
    documentTypes.includes('PAYOUT_LETTER') &&
    resolvePpsrCheckDocument(items, assetType)?.status ===
      InspectionSecurityStatusEnum.reportFound
  );
}

export function getInspectionCheckDocument(
  items: any,
  key: string,
  stepValue: string,
  assetType?: string
): any {
  if (!items) return null;

  // Backend InspectionChecks field is glassesVehicleData; the UI key remains
  // autoGrabData for historical labelling.
  if (key === 'autoGrabData') {
    return items.glassesVehicleData ?? items.autoGrabData;
  }

  if (key === 'vehiclePhotosQuality' || key === 'vehiclePhotosAuthenticity') {
    return computeVehiclePhotoStatus(
      key,
      assetType ?? '',
      items
    );
  }

  if (key === 'dataEdits') {
    // OCR edits can come from either identifier; prefer VIN when present.
    return items.vin ?? items.licencePlate;
  }

  if (key === 'ppsr') {
    return resolvePpsrCheckDocument(items, assetType);
  }

  return items[getInspectionCheckItemKey(stepValue, key)];
}

/** Statuses that normally do not open the right-hand security-check detail panel. */
const NON_OPENABLE_SECURITY_CHECK_STATUSES = [
  InspectionSecurityStatusEnum.notPerformed,
  InspectionSecurityStatusEnum.pass,
  InspectionSecurityStatusEnum.noRegister,
  InspectionSecurityStatusEnum.noReport,
  InspectionSecurityStatusEnum.noRecord,
  InspectionSecurityStatusEnum.notFound,
  InspectionSecurityStatusEnum.match,
  InspectionSecurityStatusEnum.error
] as const;

/**
 * Whether a list-row click may open the detail panel for this check status.
 * Failure-panel navigation can bypass this via `forceOpen` so blocked rules
 * still open when the underlying check status is UNREGISTERED / PASS / etc.
 */
export function canOpenSecurityCheckDetail(
  status: any,
  key: string,
  type: string,
  documentObj: any
): boolean {
  if (
    status &&
    !NON_OPENABLE_SECURITY_CHECK_STATUSES.includes(
      status as (typeof NON_OPENABLE_SECURITY_CHECK_STATUSES)[number]
    )
  ) {
    return true;
  }
  if (
    key === 'licenseAuthenticity' &&
    [
      InspectionSecurityStatusEnum.pass,
      InspectionSecurityStatusEnum.failed,
      InspectionSecurityStatusEnum.warning
    ].includes(status)
  ) {
    return true;
  }
  if (
    type === 'NEVDIS_REGISTRATION' &&
    (status === InspectionSecurityStatusEnum.noRegister ||
      status === InspectionSecurityStatusEnum.discrepancyFound) &&
    documentObj?.expiry &&
    documentObj.expiry !== ''
  ) {
    return true;
  }
  if (
    type === 'REGO_EXPIRY' &&
    [InspectionSecurityStatusEnum.pass, InspectionSecurityStatusEnum.failed].includes(
      status
    ) &&
    documentObj?.isExpired
  ) {
    return true;
  }
  return false;
}

// Extract the common logic for opening security check detail panel
export const openSecurityCheckDetail = (
  status: any,
  key: string,
  type: string,
  documentObj: any,
  isNotMatch: boolean,
  activeStep: string,
  handlers: {
    setCollapse: (value: boolean) => void;
    setActivePanel: (value: string) => void;
    setActivity: (value: boolean) => void;
    setRightBarStatus: (value: any) => void;
    setVariant: (value: string) => void;
    setStatusChipLabel: (value: string) => void;
    setShowCommentHistory: (value: boolean) => void;
  },
  registrationPaperStatus?: boolean,
  options?: { forceOpen?: boolean }
) => {
  const { 
    setCollapse, 
    setActivePanel, 
    setActivity, 
    setRightBarStatus, 
    setVariant, 
    setStatusChipLabel,
    setShowCommentHistory
  } = handlers;

  if (
    !options?.forceOpen &&
    !canOpenSecurityCheckDetail(status, key, type, documentObj)
  ) {
    return;
  }

  setCollapse(false);
  setActivePanel(key);
  setActivity(false);
  setShowCommentHistory(true);
  
  if (activeStep === '3') {
    //set right bar status for registration
    setRightBarStatus(InspectionSecurityStatusEnum.discrepancyFound);
  } else {
    setRightBarStatus(status);
  }
  
  setVariant(getVariant(status, type, documentObj, activeStep, registrationPaperStatus));
  setStatusChipLabel(getLabel(status, type, documentObj, isNotMatch, activeStep, registrationPaperStatus));
};

const VEHICLE_PHOTOS_TYPES = [
  'VEHICLE_PHOTOS',
  'VEHICLE_PHOTOS_QUALITY',
  'VEHICLE_PHOTOS_AUTHENTICITY'
];

const AI_PHOTO_CHECK_KEYS = [
  'vehiclePhotos',
  'vehiclePhotosQuality',
  'vehiclePhotosAuthenticity'
];

/** AI photo quality/authenticity — development treats Criteria Not Met as a tag, not a red failure. */
export function isAiPhotoSecurityCheck(type?: string, key?: string): boolean {
  return (
    VEHICLE_PHOTOS_TYPES.includes(type || '') ||
    AI_PHOTO_CHECK_KEYS.includes(key || '')
  );
}

/** InspectionChecks.glassesVehicleData uses VEHICLE_DATA; older payloads used AUTOGRAB_DATA. */
const VEHICLE_LOOKUP_DATA_TYPES = ['VEHICLE_DATA', 'AUTOGRAB_DATA'];

const isVehicleLookupDataType = (type?: string) =>
  !!type && VEHICLE_LOOKUP_DATA_TYPES.includes(type);

const DATA_EDIT_WARNING_MATCHES = ['DIFFERENT', 'NEAR_MATCH'];

const normalizeDataEditValue = (value?: unknown): string =>
  String(value ?? '')
    .replace(/\s+/g, '')
    .toUpperCase();

const hasComparableValue = (value?: unknown): boolean =>
  normalizeDataEditValue(value) !== '';

const normalizeDataEditStatus = (status?: unknown) => {
  const normalizedStatus = String(status ?? '').toUpperCase();

  if (['WARNING', 'WARNIN'].includes(normalizedStatus)) {
    return InspectionSecurityStatusEnum.warning;
  }

  return status as InspectionSecurityStatusEnum | undefined;
};

const getDataEditsStatus = (documentObj?: any) =>
  normalizeDataEditStatus(
    documentObj?.dataEditsStatus ??
      documentObj?.regoCertificate?.dataEditsStatus
  );

export const isDataEditWarningMatch = (match?: unknown): boolean =>
  DATA_EDIT_WARNING_MATCHES.includes(String(match ?? '').toUpperCase());

export const hasLicencePlateDataEditWarning = (
  licencePlate?: any
): boolean => {
  if (!licencePlate) return false;

  if (isDataEditWarningMatch(licencePlate.ocrMatch)) {
    return true;
  }

  const hasRegoValues =
    hasComparableValue(licencePlate.ocrRego) &&
    hasComparableValue(licencePlate.userRego);
  const hasStateValues =
    hasComparableValue(licencePlate.ocrState) &&
    hasComparableValue(licencePlate.userState);

  return (
    (hasRegoValues &&
      normalizeDataEditValue(licencePlate.ocrRego) !==
        normalizeDataEditValue(licencePlate.userRego)) ||
    (hasStateValues &&
      normalizeDataEditValue(licencePlate.ocrState) !==
        normalizeDataEditValue(licencePlate.userState))
  );
};

export function resolveVehicleDataEditsStatus(
  items?: any
): InspectionSecurityStatusEnum {
  const vinOcrMatch = items?.vin?.ocrMatch;
  const plateOcrMatch = items?.licencePlate?.ocrMatch;
  const hasPlateDataEditWarning = hasLicencePlateDataEditWarning(
    items?.licencePlate
  );

  if (
    isDataEditWarningMatch(vinOcrMatch) ||
    isDataEditWarningMatch(plateOcrMatch) ||
    hasPlateDataEditWarning
  ) {
    return InspectionSecurityStatusEnum.warning;
  }

  const requestedMatches = [vinOcrMatch, plateOcrMatch].filter(
    (status) =>
      status &&
      status !== InspectionSecurityStatusEnum.notRequested
  );

  if (
    requestedMatches.length > 0 &&
    requestedMatches.every(
      (status) => status === InspectionSecurityStatusEnum.match
    )
  ) {
    return InspectionSecurityStatusEnum.pass;
  }

  return InspectionSecurityStatusEnum.notPerformed;
}

export function isFailedInspectionRule(rule?: {
  status?: string;
  evaluation?: string;
} | null): boolean {
  return rule?.status === 'FAIL' || rule?.evaluation === 'FAIL';
}

export function getRuleTypesForSecurityCheck(
  type?: string,
  key?: string
): string[] {
  return Array.from(
    new Set(
      [
        type ? SECURITY_CHECK_TYPE_TO_RULE_TYPE[type] : undefined,
        key ? SECURITY_CHECK_TYPE_TO_RULE_TYPE[key] : undefined,
        ...(type ? SECURITY_CHECK_RULE_TYPE_ALIASES[type] || [] : []),
        ...(key ? SECURITY_CHECK_RULE_TYPE_ALIASES[key] || [] : [])
      ].filter((ruleType): ruleType is string => Boolean(ruleType))
    )
  );
}

export function findMatchingFailedRule(
  rules: InspectionRule[] | undefined,
  type?: string,
  key?: string
): InspectionRule | undefined {
  const ruleTypes = getRuleTypesForSecurityCheck(type, key);
  if (!rules?.length || ruleTypes.length === 0) return undefined;

  return rules
    .filter(isFailedInspectionRule)
    .find((rule) => ruleTypes.includes(rule.ruleType || ''));
}

export const SECURITY_CHECK_PHOTO_DOCUMENT_TYPES = [
  'ASSET_BACK',
  'ASSET_FRONT',
  'ASSET_LEFT',
  'ASSET_RIGHT',
  'MARINE_BACK',
  'MARINE_FRONT',
  'MARINE_LEFT',
  'MARINE_RIGHT',
  'INTERNAL_FRONT',
  'INTERNAL_BACK',
  'INTERNAL_BOOT',
  'INTERNAL_LIVING_AREA',
  'INTERNAL_KITCHEN',
  'INTERNAL_SLEEPING_AREA',
  'VIN',
  'REGO_PLATE',
  'OPERATING_HOURS'
];

/** Backend InspectionDocument.isExternalPhoto() — quality AI only runs on these. */
export const SECURITY_CHECK_EXTERNAL_PHOTO_DOCUMENT_TYPES = [
  'ASSET_FRONT',
  'ASSET_BACK',
  'ASSET_LEFT',
  'ASSET_RIGHT',
  'MARINE_FRONT',
  'MARINE_BACK',
  'MARINE_LEFT',
  'MARINE_RIGHT'
];

export const VEHICLE_DETAILS_SECTION_DOCUMENT_TYPES = [
  ...SECURITY_CHECK_PHOTO_DOCUMENT_TYPES,
  'MARINE_HIN',
  'MARINE_REGO_NUMBER',
  'ODOMETER',
  'SALE_DETAILS',
  'PPSR',
  'MARINE_PPSR',
  'RAV',
  'NEVDIS',
  'GLASSES'
];

export function hasVehicleDetailsSectionDocument(
  documentTypes: string[] = []
): boolean {
  return VEHICLE_DETAILS_SECTION_DOCUMENT_TYPES.some((type) =>
    documentTypes.includes(type)
  );
}

const ALWAYS_VISIBLE_WHEN_NOT_REQUESTED = new Set(['ppsr', 'rav']);

/**
 * The backend VIN / number plate cross-check (VinCheck.licencePlateMatch and
 * LicencePlateCheck.vinMatch, rule ASSET_VIN_REGO_MATCH) is only evaluated when
 * both the VIN and REGO_PLATE documents are requested. There is no HIN / marine
 * rego equivalent, so the panel is hidden for every other combination.
 */
export function hasVinPlateMatchDocuments(documentTypes: string[] = []): boolean {
  return documentTypes.includes('VIN') && documentTypes.includes('REGO_PLATE');
}

export type SecurityCheckVisibilityContext = {
  documentTypes?: string[];
  assetType?: string;
  inspectionChecks?: any;
  /** Section step value ('1'-'4'); needed to resolve step 3/4 store keys. */
  stepValue?: string;
};

export function isVisibleSecurityCheck(
  key: string,
  {
    documentTypes = [],
    assetType,
    inspectionChecks,
    stepValue = ''
  }: SecurityCheckVisibilityContext
): boolean {
  if (
    key === 'BANK_STATEMENT_ADDRESS' &&
    (!documentTypes.includes('REGO_PAPERS') ||
      !documentTypes.includes('BANK_STATEMENTS'))
  ) {
    return false;
  }

  if (key === 'vin' && !hasVinPlateMatchDocuments(documentTypes)) {
    return false;
  }

  if (
    key === 'dataEdits' &&
    !['VIN', 'REGO_PLATE'].some((type) => documentTypes.includes(type))
  ) {
    return false;
  }

  if (
    assetType === AssetTypesEnum.MARINE &&
    (key === 'rav' || key === 'dataEdits')
  ) {
    return false;
  }

  if (
    [
      'vehiclePhotos',
      'vehiclePhotosQuality',
      'vehiclePhotosAuthenticity'
    ].includes(key) &&
    !SECURITY_CHECK_PHOTO_DOCUMENT_TYPES.some((type) =>
      documentTypes.includes(type)
    )
  ) {
    return false;
  }

  // Quality AI is exterior-only; hide the quality row when no exterior photos
  // were requested (VIN/plate/internal-only inspections).
  if (
    key === 'vehiclePhotosQuality' &&
    !SECURITY_CHECK_EXTERNAL_PHOTO_DOCUMENT_TYPES.some((type) =>
      documentTypes.includes(type)
    )
  ) {
    return false;
  }

  const items = inspectionChecks ?? {};
  const documentObj = getInspectionCheckDocument(
    items,
    key,
    stepValue,
    assetType
  );

  if (
    key === 'vehiclePhotosQuality' ||
    key === 'vehiclePhotosAuthenticity'
  ) {
    if (!items.vehiclePhotos && !items.marinePhotos) {
      return false;
    }
    return documentObj?.status !== InspectionSecurityStatusEnum.notRequested;
  }

  if (!documentObj) {
    return false;
  }

  // Data edits are derived from VIN/plate OCR, not the VIN check request flag.
  if (key === 'dataEdits') {
    return true;
  }

  const status = documentObj?.status;
  if (
    status === InspectionSecurityStatusEnum.notRequested &&
    !ALWAYS_VISIBLE_WHEN_NOT_REQUESTED.has(key)
  ) {
    return false;
  }

  return true;
}

/** Returns variant key (e.g. 'success_light') or 'hidden' for list item display. */
export const getVariant = (
  status: string,
  type: string,
  documentObj: any,
  activeStep?: string,
  registrationPaperStatus?: boolean
): string => {
  if (
    type?.startsWith('LICENSE_') &&
    status === InspectionSecurityStatusEnum.notProvided
  ) {
    return SECURITY_CHECK_LIGHT.WARNING;
  }

  if (
    VEHICLE_PHOTOS_TYPES.includes(type) &&
    status !== InspectionSecurityStatusEnum.warning &&
    status !== InspectionSecurityStatusEnum.pass &&
    status !== InspectionSecurityStatusEnum.notAvailable
  ) {
    return 'hidden';
  }
  return type === 'BANK_STATEMENT_ADDRESS' &&
  status === InspectionSecurityStatusEnum.failed
    ? SECURITY_CHECK_LIGHT.WARNING
    : (type === 'PPSR_ENCUMBRANCE' || type === 'MARINE_PPSR_ENCUMBRANCE') &&
      status === InspectionSecurityStatusEnum.reportFound
    ? SECURITY_CHECK_LIGHT.SUCCESS
    : ['RAV_COMPLIANCE'].includes(type) &&
      status === InspectionSecurityStatusEnum.reportFound
    ? SECURITY_CHECK_LIGHT.SUCCESS
    : isVehicleLookupDataType(type) &&
      status === InspectionSecurityStatusEnum.reportFound
    ? SECURITY_CHECK_LIGHT.SUCCESS
    : type === 'NEVDIS_REGISTRATION' &&
      activeStep !== '3' &&
      status === InspectionSecurityStatusEnum.reportFound &&
      documentObj.regoStatus ===
        InspectionSecurityStatusEnum.registered.toLocaleUpperCase()
    ? SECURITY_CHECK_LIGHT.SUCCESS
    : ['PPSR_ENCUMBRANCE', 'MARINE_PPSR_ENCUMBRANCE'].includes(type) &&
      status === InspectionSecurityStatusEnum.noRecord
    ? SECURITY_CHECK_LIGHT.SUCCESS
    : (type === 'VEHICLE_PHOTOS' || type === 'VEHICLE_PHOTOS_QUALITY' || type === 'VEHICLE_PHOTOS_AUTHENTICITY') &&
      status === InspectionSecurityStatusEnum.notAvailable
    ? SECURITY_CHECK_LIGHT.INFO
    : type === 'NEVDIS_REGISTRATION' &&
      activeStep !== '3' &&
      status === InspectionSecurityStatusEnum.noReport
    ? SECURITY_CHECK_LIGHT.INFO
    : ['RAV_COMPLIANCE'].includes(type) &&
      status === InspectionSecurityStatusEnum.noReport
    ? SECURITY_CHECK_LIGHT.INFO
    : type === 'LICENSE_EDITS' &&
      status === InspectionSecurityStatusEnum.failed
    ? SECURITY_CHECK_LIGHT.WARNING
    : activeStep === '3' &&
      registrationPaperStatus &&
      type === 'NEVDIS_REGISTRATION'
    ? INSPECTION_SECURITY_CHECK_VARIANT[
        InspectionSecurityStatusEnum.discrepancyFound
      ]
    : type === 'VEHICLE_VIN' &&
      status === InspectionSecurityStatusEnum.discrepancyFound
    ? 'error_light'
    : INSPECTION_SECURITY_CHECK_VARIANT[
        status as InspectionSecurityStatusEnum
      ];
};

// Helper function to format strings
const stringFormat = (template: string, ...args: any[]) => {
  return template.replace(/{(\d+)}/g, function (match, number) {
    return typeof args[number] != 'undefined' ? args[number] : match;
  });
};

// Helper function to get report count label
const getReportCountLabel = (type: string, details: any[]) => {
  const count = details.length;
  return count > 1 ? `${count} Reports Found` : '1 Report Found';
};

// Copy the getLabel logic from SecurityCheck
export const getLabel = (
  status: string,
  type: string,
  documentObj: any,
  isNotMatch: boolean,
  activeStep?: string,
  registrationPaperStatus?: boolean
) => {
  // Define common status-type combinations
  const statusTypeMap = {
    'LICENSE_EXPIRY:PASS': 'Valid',
    'LICENSE_EXPIRY:FAIL': 'Expired',
    'REGO_EXPIRY:PASS': 'Match',
    'REGO_EXPIRY:FAIL': 'Discrepancy found',
    'LICENSE_EDITS:FAIL': 'Warning',
    'BANK_STATEMENT_ADDRESS:FAIL': 'Discrepancy found'
  };

  const key = `${type}:${status}`;
  if (statusTypeMap[key as keyof typeof statusTypeMap]) {
    return statusTypeMap[key as keyof typeof statusTypeMap];
  }

  // Handle NEVDIS_REGISTRATION cases
  if (type === 'NEVDIS_REGISTRATION') {
    if (activeStep !== '3') {
      if (status === InspectionSecurityStatusEnum.expired) return 'Expired';
      if (status === InspectionSecurityStatusEnum.noReport)
        return 'Not Found';
      if (
        status === InspectionSecurityStatusEnum.reportFound &&
        documentObj.regoStatus ===
          InspectionSecurityStatusEnum.registered.toLocaleUpperCase()
      ) {
        return 'Registered';
      }
    } else if (activeStep === '3' && registrationPaperStatus) {
      return INSPECTION_SECURITY_CHECK_LABEL_STATUS[
        InspectionSecurityStatusEnum.discrepancyFound
      ];
    }
  }

  // Handle stolen/written off reports
  if (
    ['NEVDIS_STOLEN', 'NEVDIS_WRITTEN_OFF'].includes(type) &&
    status === InspectionSecurityStatusEnum.reportFound
  ) {
    const details =
      type === 'NEVDIS_STOLEN'
        ? documentObj.stolenDetails
        : documentObj.writtenOffDetails;
    return getReportCountLabel(type, details);
  }

  if (isVehicleLookupDataType(type)) {
    if (status === InspectionSecurityStatusEnum.notRequested) return 'Disabled';
    if (status === InspectionSecurityStatusEnum.reportFound) return 'Received';
    if (status === InspectionSecurityStatusEnum.error) return 'Error';
  }

  // Handle PPSR and RAV cases
  if (
    [
      'PPSR_ENCUMBRANCE',
      'MARINE_PPSR_ENCUMBRANCE',
      'RAV_COMPLIANCE'
    ].includes(type)
  ) {
    if (status === InspectionSecurityStatusEnum.notRequested)
      return 'Disabled';
    if (type === 'PPSR_ENCUMBRANCE' || type === 'MARINE_PPSR_ENCUMBRANCE') {
      if (status === InspectionSecurityStatusEnum.reportFound) {
        return stringFormat(
          INSPECTION_SECURITY_CHECK_LABEL_STATUS[
            InspectionSecurityStatusEnum.registrations
          ],
          documentObj?.ppsrData?.length
        );
      }
      if (status === InspectionSecurityStatusEnum.noRecord)
        return 'No Registrations Found';
    }
    if (type === 'RAV_COMPLIANCE') {
      if (status === InspectionSecurityStatusEnum.noReport) return 'No Found';
      if (status === InspectionSecurityStatusEnum.reportFound)
        return 'Received';
    }
  }

  // Default fallback
  return INSPECTION_SECURITY_CHECK_LABEL_STATUS[
    status as keyof typeof INSPECTION_SECURITY_CHECK_LABEL_STATUS
  ];
};

// Mirrors backend getVehiclePhotosRule: any warning fails, and PASS requires
// every requested collection to have results, so pending/error outrank pass.
const PHOTO_STATUS_RANK: Record<string, number> = {
  [InspectionSecurityStatusEnum.warning]: 3,
  [InspectionSecurityStatusEnum.failed]: 3,
  [InspectionSecurityStatusEnum.notPerformed]: 2,
  [InspectionSecurityStatusEnum.notAvailable]: 2,
  [InspectionSecurityStatusEnum.error]: 2,
  [InspectionSecurityStatusEnum.pass]: 1,
  [InspectionSecurityStatusEnum.notRequested]: 0
};

function computePhotoCollectionStatus(
  photosData: any,
  key: 'vehiclePhotosQuality' | 'vehiclePhotosAuthenticity'
): any {
  const type =
    key === 'vehiclePhotosQuality'
      ? 'VEHICLE_PHOTOS_QUALITY'
      : 'VEHICLE_PHOTOS_AUTHENTICITY';

  if (!photosData) return null;

  // Backend rules ignore collections that were never requested, even if stale
  // photo results remain on the payload after setNotRequested().
  if (photosData.status === InspectionSecurityStatusEnum.notRequested) {
    return { ...photosData, type };
  }

  if (photosData.photos && Array.isArray(photosData.photos) && photosData.photos.length > 0) {
    const hasWarning = photosData.photos.some((photo: any) =>
      key === 'vehiclePhotosQuality'
        ? [
            photo.quality,
            photo.perspective,
            photo.framing
          ].includes(InspectionSecurityStatusEnum.warning) ||
          String(photo.qualityReason ?? '').trim() !== ''
        : photo.authenticity === InspectionSecurityStatusEnum.warning ||
          String(photo.authenticityReason ?? '').trim() !== ''
    );

    if (hasWarning) {
      return {
        ...photosData,
        status: InspectionSecurityStatusEnum.warning,
        type
      };
    }

    if (
      [
        InspectionSecurityStatusEnum.notRequested,
        InspectionSecurityStatusEnum.notPerformed,
        InspectionSecurityStatusEnum.notAvailable,
        InspectionSecurityStatusEnum.error
      ].includes(photosData.status)
    ) {
      return { ...photosData, type };
    }

    if (
      [
        InspectionSecurityStatusEnum.pass,
        InspectionSecurityStatusEnum.warning
      ].includes(photosData.status)
    ) {
      return {
        ...photosData,
        status: InspectionSecurityStatusEnum.pass,
        type
      };
    }

    const relevantStatuses = photosData.photos.flatMap((photo: any) =>
      key === 'vehiclePhotosQuality'
        ? [photo.quality, photo.perspective, photo.framing]
        : [photo.authenticity]
    );
    const fallbackStatus = relevantStatuses.includes(
      InspectionSecurityStatusEnum.error
    )
      ? InspectionSecurityStatusEnum.error
      : relevantStatuses.includes(InspectionSecurityStatusEnum.pass)
        ? InspectionSecurityStatusEnum.pass
        : InspectionSecurityStatusEnum.notAvailable;

    return { ...photosData, status: fallbackStatus, type };
  }

  return {
    ...photosData,
    status: photosData.status || InspectionSecurityStatusEnum.notPerformed,
    type
  };
}

const isRequestedPhotoCheck = (check: any): boolean =>
  !!check && check.status !== InspectionSecurityStatusEnum.notRequested;

function mergePhotoCheckStatuses(
  first: any,
  second: any
): any {
  if (!first) return second;
  if (!second) return first;

  // A collection the backend never requested must not mask the other one
  // (e.g. watercraft-only photos on a MARINE_WITH_TRAILER inspection).
  if (!isRequestedPhotoCheck(first) && isRequestedPhotoCheck(second)) {
    return second;
  }
  if (isRequestedPhotoCheck(first) && !isRequestedPhotoCheck(second)) {
    return first;
  }

  const firstRank = PHOTO_STATUS_RANK[first.status] ?? 0;
  const secondRank = PHOTO_STATUS_RANK[second.status] ?? 0;
  const winner = secondRank > firstRank ? second : first;

  return {
    ...first,
    ...second,
    status: winner.status,
    type: first.type || second.type,
    photos: [...(first.photos || []), ...(second.photos || [])]
  };
}

/**
 * Compute the photo quality or authenticity status object from per-photo data.
 * Used by both SecurityCheck and DetailedInfo to ensure consistent status calculation.
 */
export function computeVehiclePhotoStatus(
  key: 'vehiclePhotosQuality' | 'vehiclePhotosAuthenticity',
  assetType: string,
  inspectionChecks: any
): any {
  if (assetType === AssetTypesEnum.MARINE) {
    return computePhotoCollectionStatus(inspectionChecks.marinePhotos, key);
  }

  if (assetType === AssetTypesEnum.MARINE_WITH_TRAILER) {
    return mergePhotoCheckStatuses(
      computePhotoCollectionStatus(inspectionChecks.vehiclePhotos, key),
      computePhotoCollectionStatus(inspectionChecks.marinePhotos, key)
    );
  }

  return computePhotoCollectionStatus(inspectionChecks.vehiclePhotos, key);
}

/**
 * Determine VIN status from licencePlateMatch / vinMatch cross-check fields.
 */
export function getVinStatus(items: any, isNotMatch: boolean): InspectionSecurityStatusEnum {
  if (isNotMatch) return InspectionSecurityStatusEnum.discrepancyFound;

  const vinMatch = items?.vin?.licencePlateMatch === InspectionSecurityStatusEnum.match;
  const plateMatch = items?.licencePlate?.vinMatch === InspectionSecurityStatusEnum.match;

  if (vinMatch && plateMatch) return InspectionSecurityStatusEnum.match;

  const hasNoRecord = [items?.vin?.status, items?.licencePlate?.status].includes(
    InspectionSecurityStatusEnum.noRecord
  );

  return hasNoRecord
    ? InspectionSecurityStatusEnum.notFound
    : InspectionSecurityStatusEnum.notPerformed;
}

/**
 * Resolve the effective security check status for a given document object.
 * Handles special cases for VIN, PPSR, RAV, NEVDIS, bank/rego data edits.
 */
export function resolveSecurityCheckStatus(
  documentObj: any,
  type: string,
  items: any,
  key: string,
  isNotMatch: boolean
): InspectionSecurityStatusEnum {
  if (!documentObj) return InspectionSecurityStatusEnum.notPerformed;

  if (type === 'VEHICLE_VIN') {
    if (documentObj.status === InspectionSecurityStatusEnum.notRequested) {
      return InspectionSecurityStatusEnum.notRequested;
    }
    return getVinStatus(items, isNotMatch);
  }

  if (
    (type === REGO_CERTIFICATE_DATA_EDIT_TYPE ||
      type === BANK_STATEMENT_DATA_EDIT_TYPE ||
      ['REGO_CERTIFICATE', 'BANK_STATEMENT'].includes(type)) &&
    [
      RegoSecurityCheckEnum.REGO_SECURITY_DATA_EDIT,
      BankSecurityCheckEnum.BANK_SECURITY_DATA_EDIT
    ].includes(key as any)
  ) {
    return getDataEditsStatus(documentObj);
  }

  if (
    type === 'NEVDIS_REGISTRATION' &&
    documentObj.status === InspectionSecurityStatusEnum.notPerformed &&
    [items?.vin?.status, items?.licencePlate?.status].includes(
      InspectionSecurityStatusEnum.noRecord
    )
  ) {
    return InspectionSecurityStatusEnum.notFound;
  }

  if (type === 'PPSR_ENCUMBRANCE' || type === 'MARINE_PPSR_ENCUMBRANCE') {
    return documentObj.ppsrData?.length > 0
      ? InspectionSecurityStatusEnum.reportFound
      : documentObj.status;
  }

  if (type === 'RAV_COMPLIANCE') {
    return documentObj.status === InspectionSecurityStatusEnum.pass
      ? InspectionSecurityStatusEnum.reportFound
      : documentObj.status;
  }

  if (['NEVDIS_STOLEN', 'NEVDIS_WRITTEN_OFF'].includes(type)) {
    const details =
      type === 'NEVDIS_STOLEN' ? documentObj.stolenDetails : documentObj.writtenOffDetails;
    return details?.length > 0 ? InspectionSecurityStatusEnum.reportFound : documentObj.status;
  }

  return documentObj?.status;
}

/**
 * Resolve the left-menu flag variant for a given security check.
 * Returns a StatusChipVariant string used by DetailedInfo to colour the menu icon.
 *
 * @param key               - The check key (e.g. 'rav', 'ppsr')
 * @param status            - The resolved status value
 * @param type              - The document type string (e.g. 'NEVDIS_REGISTRATION')
 * @param stepValue         - The active step number as a string ('1'–'7')
 * @param documentObj       - The raw document object from inspectionChecks
 * @param registrationPaperStatus - Whether rego/marine cert papers are present
 * @param rules               - The rules array from inspectionChecks
 */
export function resolveMenuItemFlag(
  key: string,
  status: string,
  type: string,
  stepValue: string,
  documentObj: any,
  registrationPaperStatus: boolean,
  rules?: InspectionRule[]
): string | undefined {
  const matchingRule = findMatchingFailedRule(rules, type, key);
  if (matchingRule?.exception === 'EXCEPTION_GRANTED') {
    return SECURITY_CHECK_LIGHT.SUCCESS;
  }
  // EXCEPTION_REQUESTED is not blocking, but the underlying check
  // still did not pass — fall through to warning/error from status.
  // AI photo rules stay on the check status (Warning → orange). Development
  // never promoted photo Criteria Not Met to a red section alert.
  if (
    matchingRule &&
    matchingRule.exception !== 'EXCEPTION_REQUESTED' &&
    !isAiPhotoSecurityCheck(type, key)
  ) {
    return SECURITY_CHECK_LIGHT.ERROR;
  }

  // Disabled and Not Performed checks remain visible in the check list but
  // do not participate in the section indicator — same as passed.
  if (
    status === InspectionSecurityStatusEnum.notRequested ||
    status === InspectionSecurityStatusEnum.notPerformed
  ) {
    return undefined;
  }

  if (
    stepValue === '1' &&
    type?.startsWith('LICENSE_') &&
    status === InspectionSecurityStatusEnum.notProvided
  ) {
    return SECURITY_CHECK_LIGHT.WARNING;
  }

  // Compliance "Not Found" is informational and has no backend rule, so it
  // must not suppress the section tick when every other check passed.
  if (
    (key === NevdisSecurityCheckEnum.NEVDIS_SECURITY_RAV ||
      type === 'RAV_COMPLIANCE') &&
    (status === InspectionSecurityStatusEnum.noRecord ||
      status === InspectionSecurityStatusEnum.noReport)
  ) {
    return undefined;
  }

  if (type === 'VEHICLE_VIN') {
    if (status === InspectionSecurityStatusEnum.discrepancyFound) {
      return SECURITY_CHECK_LIGHT.ERROR;
    }
  }

  if (['PPSR_ENCUMBRANCE', 'MARINE_PPSR_ENCUMBRANCE'].includes(type)) {
    // Encumbrance is informational until the template actually fails
    // ASSET_PPSR_ENCUMBRANCE. BLOCKED / APPROVAL_REQUIRED already returned
    // ERROR above; EXCEPTION_REQUESTED keeps a warning.
    if (status === InspectionSecurityStatusEnum.reportFound) {
      return matchingRule
        ? SECURITY_CHECK_LIGHT.WARNING
        : SECURITY_CHECK_LIGHT.SUCCESS;
    }
    if (status === InspectionSecurityStatusEnum.noRecord) return SECURITY_CHECK_LIGHT.SUCCESS;
  }

  if (
    type === 'RAV_COMPLIANCE' &&
    status === InspectionSecurityStatusEnum.reportFound
  ) {
    return SECURITY_CHECK_LIGHT.SUCCESS;
  }

  if (
    isVehicleLookupDataType(type) &&
    status === InspectionSecurityStatusEnum.reportFound
  ) {
    return SECURITY_CHECK_LIGHT.SUCCESS;
  }

  if (type === 'BANK_STATEMENT_ADDRESS' && status === InspectionSecurityStatusEnum.failed) {
    return SECURITY_CHECK_LIGHT.WARNING;
  }

  if (type === 'NEVDIS_REGISTRATION') {
    if (stepValue !== '3') {
      // Backend RecordFound already means registered and not expired.
      if (status === InspectionSecurityStatusEnum.reportFound) {
        return SECURITY_CHECK_LIGHT.SUCCESS;
      }
      if (status === InspectionSecurityStatusEnum.noReport) return SECURITY_CHECK_LIGHT.INFO;
    }
    if (stepValue === '3' && registrationPaperStatus) {
      return INSPECTION_SECURITY_CHECK_VARIANT[InspectionSecurityStatusEnum.discrepancyFound];
    }
  }

  if (type === 'LICENSE_EDITS' && status === InspectionSecurityStatusEnum.failed) {
    return SECURITY_CHECK_LIGHT.WARNING;
  }

  return INSPECTION_SECURITY_CHECK_VARIANT[status as InspectionSecurityStatusEnum];
}

/** Numeric section flag for left-nav StatusListItem (higher = more severe) */
export const DETAIL_VIEW_FLAG_STATUS = {
  INFO: 0,
  SUCCESS: 1,
  WARNING: 2,
  ERROR: 3
} as const;

export type DetailViewFlagStatus =
  (typeof DETAIL_VIEW_FLAG_STATUS)[keyof typeof DETAIL_VIEW_FLAG_STATUS];

/**
 * Security check keys that feed a left-nav section indicator.
 * A MARINE_REGO_CERT-only registration section never runs rego checks
 * (backend reports it as Received once uploaded), so it has none.
 */
export function getSectionCheckKeys(
  stepValue: string,
  documentTypes: string[] = []
): string[] {
  if (stepValue === '3' && !documentTypes.includes('REGO_PAPERS')) {
    return [];
  }
  return SECURITY_CHECK_LIST[parseInt(stepValue, 10) - 1] || [];
}

export function getDocumentOnlySectionFlag(
  stepValue: string,
  documents: Record<string, any> = {}
): DetailViewFlagStatus | undefined {
  if (
    stepValue === '5' &&
    (documents.ROADWORTHY_CERT?.isNotProvided ||
      documents.SAFETY_CERT?.isNotProvided)
  ) {
    return DETAIL_VIEW_FLAG_STATUS.WARNING;
  }

  if (stepValue === '6' && documents.PAYOUT_LETTER?.isNotProvided) {
    return DETAIL_VIEW_FLAG_STATUS.WARNING;
  }

  return undefined;
}

/**
 * Aggregate check flags into a section indicator.
 * Errors and warnings take precedence; success is returned when every
 * applicable check succeeded, or when every visible check was disregarded
 * (Disabled / Not Performed). Undefined flags represent those checks.
 */
export function aggregateSectionFlagSeverity(
  flags: Array<string | undefined | null>
): DetailViewFlagStatus {
  const effectiveFlags = flags.filter(
    (flag): flag is string => flag != null
  );

  if (!effectiveFlags.length) {
    return DETAIL_VIEW_FLAG_STATUS.SUCCESS;
  }

  if (effectiveFlags.includes(SECURITY_CHECK_LIGHT.ERROR)) {
    return DETAIL_VIEW_FLAG_STATUS.ERROR;
  }
  if (effectiveFlags.includes(SECURITY_CHECK_LIGHT.WARNING)) {
    return DETAIL_VIEW_FLAG_STATUS.WARNING;
  }
  if (
    effectiveFlags.every(
      (flag) => flag === SECURITY_CHECK_LIGHT.SUCCESS
    )
  ) {
    return DETAIL_VIEW_FLAG_STATUS.SUCCESS;
  }

  return DETAIL_VIEW_FLAG_STATUS.INFO;
}

export type SectionIndicatorState = {
  success: boolean;
  alertVariant: 'error' | 'warning';
  hideIcon: boolean;
};

export function getSectionIndicatorState(
  flag: DetailViewFlagStatus,
  documentsComplete: boolean
): SectionIndicatorState {
  const isAlert =
    flag === DETAIL_VIEW_FLAG_STATUS.ERROR ||
    flag === DETAIL_VIEW_FLAG_STATUS.WARNING;

  return {
    success:
      documentsComplete && flag === DETAIL_VIEW_FLAG_STATUS.SUCCESS,
    alertVariant:
      flag === DETAIL_VIEW_FLAG_STATUS.ERROR ? 'error' : 'warning',
    // Incomplete docs suppress only the green tick. Existing warnings/failures
    // must remain visible (backend fails rules as soon as any warning exists).
    hideIcon:
      flag === DETAIL_VIEW_FLAG_STATUS.INFO ||
      (!documentsComplete && !isAlert)
  };
}
